import { getObjectSchema } from '../Hasp/properties/v2/wireFormat.js';
import { themePropertyDefinition } from './themeV2Properties.js';
import { parseThemeJsonl, rgb565ToHex } from './themeModel.js';

// The external LVGL 7 format uses the firmware's widget/part names, while the
// canvas uses the normalized object schema names. Keep this translation here,
// at the boundary, so widgets and the property editor can keep using the same
// resolved style cascade as bundled themes.
const WIDGET_MAP = {
    scr: 'page',
    // The normalized page object is the LVGL screen itself. The separate
    // firmware `page` application describes the page container and must not
    // overwrite the screen style on the canvas.
    page: null,
    cont: 'obj',
    btnm: 'btnmatrix',
    sw: 'switch',
    cb: 'checkbox',
    ddlist: 'dropdown',
    mbox: 'msgbox',
    lmeter: 'linemeter',
};

const PART_MAP = {
    main: 'main', bg: 'main',
    indic: 'indicator',
    knob: 'knob',
    bullet: 'bullet',
    btn: 'item_buttons',
    btn_bg: 'items',
    tab_bg: 'items',
    tab_btn: 'item_buttons',
    selected: 'selected',
    scrollbar: 'scrollbar',
    series_bg: 'series_bg',
    cursor: 'cursor',
    series: 'series',
    cell1: 'cell1', cell2: 'cell2', cell3: 'cell3', cell4: 'cell4',
    scrollable: 'scrollable',
    edge_flash: 'edge_flash',
    list: 'item_buttons',
    header: 'header',
    date: 'date',
    day_names: 'day_names',
    major: 'major_ticks',
    needle: 'needle',
    placeholder: 'placeholder',
    content_scrollable: 'content_scrollable',
};

const STATE_MAP = {
    d: 'default', c: 'checked', p: 'pressed', pc: 'pressedChecked',
    i: 'disabled', ic: 'disabledChecked', f: 'focused',
    fp: 'focusedPressed',
};

const STATE_MASK = {
    d: 0, c: 0x01, f: 0x02, e: 0x04, p: 0x10, i: 0x20,
    pc: 0x11, fe: 0x06, ic: 0x21, fp: 0x12,
};

const PREVIEW_STATES = ['d', 'c', 'p', 'pc', 'i', 'ic', 'f', 'fp'];
// openhasp-next binds theme fonts to four compiled font slots. The default
// slots are Robotocondensed 12/16/24/32px, with the selected small/default
// slots matching lv_conf_v7.h for each display class.
// The metadata width is the only display-size hint available in a JSONL theme,
// so use it as the preview's best firmware-parity signal.
// The repository ships Roboto-Regular for the browser preview; firmware's
// stock build uses the equivalent Robotocondensed family in these slots.
export const PREVIEW_THEME_FONT_FAMILY = 'roboto';

export function previewThemeFontSizes(width) {
    const screenWidth = Number(width) || 240;
    if (screenWidth >= 480) return { small: 24, normal: 32, subtitle: 32, title: 32 };
    if (screenWidth >= 320) return { small: 16, normal: 24, subtitle: 24, title: 24 };
    return { small: 12, normal: 16, subtitle: 16, title: 16 };
}

function targetWidget(widget) {
    return Object.prototype.hasOwnProperty.call(WIDGET_MAP, widget) ? WIDGET_MAP[widget] : widget;
}

function targetPart(widget, part) {
    // Some firmware part names mean different normalized parts depending on
    // the widget. These explicit cases mirror the schema's actual parts.
    if (widget === 'mbox' && part === 'btn') return 'item_buttons';
    if (widget === 'mbox' && part === 'btn_bg') return 'items';
    if (widget === 'ddlist' && part === 'list') return 'item_buttons';
    return PART_MAP[part] ?? part;
}

function valueForProperty(property, document, fontSizes) {
    const definition = themePropertyDefinition(property.k);
    const color = definition.type === 'color';
    let value = property.v;

    if (color) {
        if (typeof value === 'string') value = document.indexes.colors.get(value)?.v;
        if (typeof value === 'number') return rgb565ToHex(value);
        return undefined;
    }
    if ((property.k === 'tf' || property.k === 'vf') && typeof value === 'string') {
        return fontSizes[value] ?? undefined;
    }
    if (property.k === 'r' && value === 'circle') return 999;
    if (property.k === 'bgd') {
        return { none: 'none', hor: 'horizontal', ver: 'vertical' }[value] ?? value;
    }
    return value;
}

function styleEntries(document, widget, part) {
    const entries = [];
    for (const record of document.records) {
        if (record.type !== 'a' || record.data.w !== widget || record.data.p !== part) continue;
        for (const styleId of record.data.x ?? []) {
            const style = document.indexes.styles.get(styleId);
            if (style) entries.push(style);
        }
    }
    // LVGL inserts each applied style at the head of the list. The first
    // candidate below therefore has the highest runtime precedence.
    return entries.reverse();
}

function resolveProperty(entries, key, state) {
    const candidates = entries.flatMap(style => style.properties.filter(property => property.k === key));
    const exact = candidates.find(property => property.q === state);
    if (exact) return exact;

    const requestedMask = STATE_MASK[state] ?? 0;
    let best;
    for (const property of candidates) {
        const mask = STATE_MASK[property.q];
        if (mask === undefined || (mask & requestedMask) !== mask) continue;
        if (!best || mask > STATE_MASK[best.q]) best = property;
    }
    return best;
}

function cloneValue(value) {
    return value && typeof value === 'object' ? { ...value } : value;
}

/**
 * Convert an external LVGL 7 JSONL theme to the normalized theme shape used
 * by StyleResolver. The source document remains lossless in IndexedDB; this
 * object is only the live canvas/property-panel representation.
 */
export function externalThemeToPreviewTheme(text) {
    const document = parseThemeJsonl(text);
    const theme = {};
    const fontSizes = previewThemeFontSizes(document.metadata?.w);

    for (const application of document.records.filter(record => record.type === 'a').map(record => record.data)) {
        const objectType = targetWidget(application.w);
        if (!objectType) continue;
        const schema = getObjectSchema(objectType);
        if (!schema) continue;
        const part = targetPart(application.w, application.p);
        const partDef = schema.parts?.[part];
        if (!partDef) continue;

        const entries = styleEntries(document, application.w, application.p);
        if (!entries.length) continue;
        const knownProperties = new Set(entries.flatMap(style => style.properties.map(property => property.k)));
        const target = (theme[objectType] ??= { styles: {} });
        const style = (target.styles[part] ??= {});

        for (const wireProperty of knownProperties) {
            const definition = themePropertyDefinition(wireProperty);
            const propertyName = definition.semanticName;
            if (!(propertyName in (partDef.properties ?? {}))) continue;

            const defaultProperty = resolveProperty(entries, wireProperty, 'd');
            const defaultValue = defaultProperty && valueForProperty(defaultProperty, document, fontSizes);
            if (defaultValue !== undefined) {
                style[propertyName] = cloneValue(defaultValue);
                if (wireProperty === 'tf' && 'text_font_family' in (partDef.properties ?? {})) {
                    style.text_font_family = PREVIEW_THEME_FONT_FAMILY;
                }
            }

            for (const wireState of PREVIEW_STATES.slice(1)) {
                const property = resolveProperty(entries, wireProperty, wireState);
                const value = property && valueForProperty(property, document, fontSizes);
                if (value === undefined) continue;
                const semanticState = STATE_MAP[wireState];
                style.states ??= {};
                style.states[semanticState] ??= {};
                style.states[semanticState][propertyName] = cloneValue(value);
                if (wireProperty === 'tf' && 'text_font_family' in (partDef.properties ?? {})) {
                    style.states[semanticState].text_font_family = PREVIEW_THEME_FONT_FAMILY;
                }
            }
        }
    }

    return theme;
}
